import React, { useEffect, useState } from 'react';
import { useParams, Navigate, useLocation, Link } from 'react-router-dom';
import {
  Plus, Filter, Download, Activity, ArrowRight, Sparkles, ChevronRight,
} from 'lucide-react';
import DashboardShell from '@/components/layout/DashboardShell';
import { moduleBySlug } from '@/lib/modules';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';

// Tools per module — first tool ALWAYS the Records page; subsequent ones link to /sub/...
export const MODULE_TOOLS = {
  events: [
    { key: 'records', label: 'Events', desc: 'Plan and configure events', live: true, route: '/records' },
    { key: 'tickets', label: 'Tickets', desc: 'Issue and validate tickets', live: true, route: '/sub/tickets' },
    { key: 'access', label: 'QR check-in', desc: 'Gate scanners', live: true, route: '/sub/access' },
    { key: 'certs', label: 'Digital certificates', desc: 'Tamper-proof, QR-validated', live: true, route: '/sub/certs' },
    { key: 'comms', label: 'Mass invites', desc: 'WhatsApp · SMS · Email', live: true, route: '/sub/comms' },
    { key: 'analytics', label: 'Attendance analytics', desc: 'Live flow & demographics', live: false },
  ],
  engineering: [
    { key: 'records', label: 'Work Orders', desc: 'Active jobs & assignments', live: true, route: '/records' },
    { key: 'service-requests', label: 'Service Requests', desc: 'Inbound client requests', live: true, route: '/sub/service-requests' },
    { key: 'dispatch', label: 'Engineer dispatch', desc: 'Routing with GPS', live: true, route: '/sub/dispatch' },
    { key: 'estimates', label: 'Estimates & quotes', desc: 'Auto-priced quotes', live: true, route: '/sub/estimates' },
    { key: 'sign-off', label: 'Digital sign-off', desc: 'Service report & receipt', live: true, route: '/sub/sign-off' },
    { key: 'inventory', label: 'Parts inventory', desc: 'Stock & consumables', live: true, route: '/sub/inventory' },
  ],
  financials: [
    // === FINANCIAL MANAGEMENT ===
    { key: 'records', label: 'Records (All)', desc: 'Central record store across all financials & asset tools', live: true, route: '/records', group: 'Financial Management' },
    { key: 'accounts', label: 'Chart of Accounts', desc: 'Account codes & structure', live: true, route: '/sub/accounts', group: 'Financial Management' },
    { key: 'transactions', label: 'Transactions & Journals', desc: 'Daily postings & journal entries', live: true, route: '/sub/transactions', group: 'Financial Management' },
    { key: 'invoices', label: 'Invoices', desc: 'Multi-item invoices with logo & PDF', live: true, route: '/sub/invoices', group: 'Financial Management' },
    { key: 'receipts', label: 'Receipts', desc: 'Auto-generated payment receipts', live: true, route: '/sub/receipts', group: 'Financial Management' },
    { key: 'cashbook', label: 'Cash Book', desc: 'Cash inflow/outflow ledger', live: true, route: '/sub/cashbook', group: 'Financial Management' },
    { key: 'budget', label: 'Budget & Expenses', desc: 'Approved budgets vs spend', live: true, route: '/sub/budget', group: 'Financial Management' },
    { key: 'bank-recon', label: 'Bank Reconciliation', desc: 'Match bank statements to ledger', live: true, route: '/sub/bank-recon', group: 'Financial Management' },
    { key: 'reports', label: 'Financial Reports', desc: 'Saved reports library', live: true, route: '/sub/reports', group: 'Financial Management' },

    // === ASSET MANAGEMENT ===
    { key: 'asset-register', label: 'Asset Register', desc: 'Single source for every asset entry', live: true, route: '/sub/asset-register', group: 'Asset Management' },
    { key: 'asset-allocation', label: 'Asset Allocation', desc: 'Who has what, where', live: true, route: '/sub/asset-allocation', group: 'Asset Management' },
    { key: 'depreciation', label: 'Depreciation', desc: 'Per-method asset depreciation', live: true, route: '/sub/depreciation', group: 'Asset Management' },
    { key: 'disposal', label: 'Disposal', desc: 'Damaged · sold · written-off', live: true, route: '/sub/disposal', group: 'Asset Management' },
    { key: 'asset-count', label: 'Asset Count', desc: 'Periodic stocktake & verification', live: true, route: '/sub/asset-count', group: 'Asset Management' },
    { key: 'asset-reports', label: 'Asset Reports', desc: 'Status · location · condition reports', live: true, route: '/sub/asset-reports', group: 'Asset Management' },

    // === PROCUREMENT & STORE ===
    { key: 'vendors', label: 'Vendor Directory', desc: 'Approved suppliers list', live: true, route: '/sub/vendors', group: 'Procurement & Store' },
    { key: 'purchase-orders', label: 'Purchase Orders', desc: 'POs with auto-numbering', live: true, route: '/sub/purchase-orders', group: 'Procurement & Store' },
    { key: 'rfq', label: 'RFQ & Quotations', desc: 'Request for quotations', live: true, route: '/sub/rfq', group: 'Procurement & Store' },
    { key: 'vendor-invoices', label: 'Vendor Invoices', desc: 'Inbound supplier bills', live: true, route: '/sub/vendor-invoices', group: 'Procurement & Store' },

    // === MAINTENANCE ===
    { key: 'work-orders', label: 'Work Orders', desc: 'Active maintenance jobs', live: true, route: '/sub/work-orders', group: 'Maintenance' },
    { key: 'pm-schedule', label: 'PM Schedule', desc: 'Planned preventive maintenance', live: true, route: '/sub/pm-schedule', group: 'Maintenance' },
    { key: 'fault-log', label: 'Fault Log', desc: 'Reported faults & resolution', live: true, route: '/sub/fault-log', group: 'Maintenance' },
    { key: 'technicians', label: 'Technicians', desc: 'In-house and contracted techs', live: true, route: '/sub/technicians', group: 'Maintenance' },

    // === OPERATIONS ===
    { key: 'departments', label: 'Departments', desc: 'Organisational structure', live: true, route: '/sub/departments', group: 'Operations' },
    { key: 'staff', label: 'Staff & Assignments', desc: 'Staff register linked to assets', live: true, route: '/sub/staff', group: 'Operations' },
    { key: 'kpi', label: 'KPI Tracker', desc: 'Department & staff KPIs', live: true, route: '/sub/kpi', group: 'Operations' },
    { key: 'tasks', label: 'CRM & Tasks', desc: 'Customer relationships & tasks', live: true, route: '/sub/tasks', group: 'Operations' },

    // === REPORTING & INTELLIGENCE ===
    { key: 'exec-dashboard', label: 'Executive Dashboard', desc: 'Top-line management view', live: true, route: '/sub/exec-dashboard', group: 'Reporting & Intelligence' },
    { key: 'pl-statement', label: 'P&L Statement', desc: 'Profit & loss for the period', live: true, route: '/sub/pl-statement', group: 'Reporting & Intelligence' },
    { key: 'balance-sheet', label: 'Balance Sheet', desc: 'Assets · liabilities · equity', live: true, route: '/sub/balance-sheet', group: 'Reporting & Intelligence' },
    { key: 'cashflow', label: 'Cash Flow', desc: 'Inflow & outflow analysis', live: true, route: '/sub/cashflow', group: 'Reporting & Intelligence' },
    { key: 'audit-trail', label: 'Audit Trail', desc: 'Immutable system log', live: true, route: '/sub/audit-trail', group: 'Reporting & Intelligence' },

    // === AI TOOLS ===
    { key: 'ai-budget', label: 'AI Budget Builder', desc: 'Generate full budgets per sector preset', live: true, route: '/sub/ai-budget', group: 'AI Tools' },
  ],
  lms: [
    { key: 'records', label: 'Learners', desc: 'Roster & profiles', live: true, route: '/records' },
    { key: 'cohorts', label: 'Cohorts', desc: 'Schedules & facilitators', live: true, route: '/sub/cohorts' },
    { key: 'curriculum', label: 'AI Curriculum', desc: 'Adaptive learning paths', live: true, route: '/sub/curriculum' },
    { key: 'assessments', label: 'Assessments / CBT', desc: 'Auto-graded tests', live: true, route: '/sub/assessments' },
    { key: 'certs', label: 'Certificates', desc: 'Issued credentials', live: true, route: '/sub/certs' },
    { key: 'analytics', label: 'Learner analytics', desc: 'Engagement & mastery', live: false },
  ],
  'corporate': [
    { key: 'records', label: 'Leads', desc: 'Inbound enquiries', live: true, route: '/records' },
    { key: 'site', label: 'Brand site', desc: 'Public pages & SEO', live: true, route: '/sub/site' },
    { key: 'catalog', label: 'Service catalog', desc: 'Productized services', live: true, route: '/sub/catalog' },
    { key: 'content', label: 'Content studio', desc: 'Blog & case studies', live: true, route: '/sub/content' },
    { key: 'booking', label: 'Discovery calls', desc: 'Calendar integration', live: true, route: '/sub/booking' },
    { key: 'analytics', label: 'Site analytics', desc: 'Traffic & conversion', live: false },
  ],
  'digital-laboratory': [
    { key: 'records', label: 'Projects', desc: 'All your studio projects', live: true, route: '/records' },
    { key: 'editor', label: 'Video editor', desc: 'Multi-track timeline', live: true, route: '/sub/editor' },
    { key: 'color', label: 'Color grading', desc: 'Wheels · curves · LUTs', live: true, route: '/sub/color' },
    { key: 'script', label: 'AI Script Studio', desc: 'Generate screenplays', live: true, route: '/sub/script' },
    { key: 'image', label: 'Image studio', desc: 'BG removal · upscale', live: true, route: '/sub/image' },
    { key: 'audio', label: 'Audio suite', desc: 'Mix · auto-subtitle', live: true, route: '/sub/audio' },
  ],
  'buy-sell': [
    { key: 'records', label: 'Listings', desc: 'Active marketplace listings', live: true, route: '/records' },
    { key: 'orders', label: 'Orders', desc: 'Buyer transactions & escrow', live: true, route: '/sub/orders' },
    { key: 'identity', label: 'Identity verification', desc: 'CAC/NIN-checked sellers', live: true, route: '/sub/identity' },
    { key: 'logistics', label: 'Delivery routing', desc: 'Riders & tracking', live: true, route: '/sub/logistics' },
    { key: 'disputes', label: 'Dispute resolution', desc: 'Two-sided evidence flow', live: true, route: '/sub/disputes' },
    { key: 'returns', label: 'Returns & refunds', desc: '7-step return flow', live: true, route: '/sub/returns' },
  ],
  education: [
    { key: 'records', label: 'Students', desc: 'Roster & profiles', live: true, route: '/records' },
    { key: 'classes', label: 'Classes & Arms', desc: 'Levels and capacity', live: true, route: '/sub/classes' },
    { key: 'fee-schedules', label: 'Fee Schedules', desc: 'Define fees per class', live: true, route: '/sub/fee-schedules' },
    { key: 'fee-bills', label: 'Fee Bills', desc: 'Per-student invoicing', live: true, route: '/sub/fee-bills' },
    { key: 'attendance', label: 'Attendance', desc: 'Daily class register', live: true, route: '/sub/attendance' },
    { key: 'report-cards', label: 'Report Cards', desc: 'Auto-compiled grades', live: true, route: '/sub/report-cards' },
    { key: 'cbt', label: 'CBT', desc: 'Computer-based testing', live: true, route: '/sub/cbt' },
    { key: 'parents', label: 'Parent Portal', desc: 'Pay fees · message teachers', live: true, route: '/sub/parents' },
    { key: 'transport', label: 'Transport & ID', desc: 'Bus routes & ID cards', live: true, route: '/sub/transport' },
  ],
  estate: [
    { key: 'records', label: 'Properties', desc: 'Plots, blocks, units', live: true, route: '/records' },
    { key: 'levies', label: 'Service Levies', desc: 'Bills & collection', live: true, route: '/sub/levies' },
    { key: 'visitors', label: 'Visitor Log', desc: 'Gate management', live: true, route: '/sub/visitors' },
    { key: 'utilities', label: 'Utility Billing', desc: 'Meters → bills', live: true, route: '/sub/utilities' },
    { key: 'security', label: 'Security & Patrol', desc: 'Patrol logs & incidents', live: true, route: '/sub/security' },
    { key: 'community', label: 'Community Board', desc: 'Notices & voting', live: true, route: '/sub/community' },
  ],
  offices: [
    { key: 'records', label: 'Employees', desc: 'HR records & contracts', live: true, route: '/records' },
    { key: 'leave-requests', label: 'Leave', desc: 'Requests & approvals', live: true, route: '/sub/leave-requests' },
    { key: 'tasks', label: 'Tasks', desc: 'Work assignments', live: true, route: '/sub/tasks' },
    { key: 'crm', label: 'CRM', desc: 'Leads & pipeline', live: true, route: '/sub/crm' },
    { key: 'procurement', label: 'Procurement', desc: 'POs & vendor mgmt', live: true, route: '/sub/procurement' },
    { key: 'visitors', label: 'Front desk', desc: 'Visitor pre-reg', live: true, route: '/sub/visitors' },
  ],
  'church-ngo': [
    { key: 'records', label: 'Members', desc: 'Membership directory', live: true, route: '/records' },
    { key: 'donations', label: 'Donations', desc: 'Tithes · offerings · grants', live: true, route: '/sub/donations' },
    { key: 'pastoral', label: 'Pastoral care', desc: 'Visit logs & follow-ups', live: true, route: '/sub/pastoral' },
    { key: 'welfare', label: 'Welfare', desc: 'Cases & distributions', live: true, route: '/sub/welfare' },
    { key: 'projects', label: 'Projects', desc: 'Building fund tracking', live: true, route: '/sub/projects' },
    { key: 'services', label: 'Services & Events', desc: 'Service planning', live: true, route: '/sub/services' },
  ],
  healthcare: [
    { key: 'records', label: 'Patients (EMR)', desc: 'Electronic medical records', live: true, route: '/records' },
    { key: 'opd-queue', label: 'OPD Queue', desc: 'Live consult queue', live: true, route: '/sub/opd-queue' },
    { key: 'vitals', label: 'Vitals Flowsheet', desc: 'BP · temp · pulse · SpO₂', live: true, route: '/sub/vitals' },
    { key: 'lab', label: 'Lab orders', desc: 'Order → result → report', live: true, route: '/sub/lab' },
    { key: 'pharmacy', label: 'Pharmacy', desc: 'Dispense & inventory', live: true, route: '/sub/pharmacy' },
    { key: 'ward', label: 'Ward & beds', desc: 'Admissions & rounds', live: true, route: '/sub/ward' },
    { key: 'billing', label: 'Billing & HMO', desc: 'Auto-bill + claims', live: true, route: '/sub/billing' },
    { key: 'theatre', label: 'Theatre / OT', desc: 'Scheduling & checklists', live: true, route: '/sub/theatre' },
  ],
  // Additional new modules
  hr: [
    { key: 'workspace', label: 'Open HR Workspace', desc: 'Full ATTP-HR HCM (separate route)', live: true, route: '' },
  ],
  retail: [
    { key: 'records', label: 'Products', desc: 'SKU catalog', live: true, route: '/records' },
    { key: 'sales', label: 'Sales / POS', desc: 'Receipts & tendering', live: true, route: '/sub/sales' },
    { key: 'loyalty', label: 'Loyalty', desc: 'Tiers · points · vouchers', live: true, route: '/sub/loyalty' },
    { key: 'promotions', label: 'Promotions', desc: 'BOGO · time-bound offers', live: true, route: '/sub/promotions' },
    { key: 'suppliers', label: 'Suppliers', desc: 'Vendor mgmt', live: true, route: '/sub/suppliers' },
    { key: 'reports', label: 'Z-reports & EOD', desc: 'End-of-day summary', live: true, route: '/sub/reports' },
  ],
  restaurant: [
    { key: 'records', label: 'Menu', desc: 'Items & modifiers', live: true, route: '/records' },
    { key: 'orders', label: 'Orders', desc: 'Dine-in · takeaway · delivery', live: true, route: '/sub/orders' },
    { key: 'kitchen', label: 'Kitchen routing', desc: 'Tickets to stations', live: true, route: '/sub/kitchen' },
    { key: 'tables', label: 'Tables / Floor', desc: 'Live floor map', live: true, route: '/sub/tables' },
    { key: 'delivery', label: 'Rider dispatch', desc: 'GPS & POD', live: true, route: '/sub/delivery' },
    { key: 'pricing', label: 'Time-based pricing', desc: 'Happy hours · sets', live: true, route: '/sub/pricing' },
  ],
  government: [
    { key: 'records', label: 'Cases', desc: 'Citizen case management', live: true, route: '/records' },
    { key: 'case-actions', label: 'Case Actions', desc: 'Timeline & outcomes', live: true, route: '/sub/case-actions' },
    { key: 'services', label: 'Citizen services', desc: 'Applications · payments', live: true, route: '/sub/services' },
    { key: 'policy', label: 'Policy chains', desc: 'Multi-level approvals', live: true, route: '/sub/policy' },
    { key: 'budget', label: 'Budget & spend', desc: 'Vote books & releases', live: true, route: '/sub/budget' },
    { key: 'revenue', label: 'Revenue & TIN', desc: 'Tax-aware collection', live: true, route: '/sub/revenue' },
  ],
};

export default function ModuleDashboard() {
  const { slug } = useParams();
  const module = moduleBySlug(slug);
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const location = useLocation();

  // Scroll to top when entering a module
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [slug, location.pathname]);

  useEffect(() => {
    if (!module) return;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get(`/m/${slug}/summary`),
          api.get(`/m/${slug}/records`, { params: { limit: 5 } }),
        ]);
        setSummary(s.data); setRecent(r.data.items || []);
      } catch (e) { console.error(e); }
    })();
  }, [slug, module]);

  if (!module) {
    // Non-existent slug — redirect safely (avoid /control-panel for non-super-admin)
    const validMods = (user?.modules || []).filter((s) => moduleBySlug(s));
    if (validMods[0]) return <Navigate to={`/dashboard/${validMods[0]}`} replace />;
    return <Navigate to={user?.role === 'super_admin' ? '/control-panel' : '/team'} replace />;
  }

  // Access enforcement (frontend)
  if (user?.role !== 'super_admin') {
    const allowed = (user?.modules || []).includes(slug);
    if (!allowed) {
      const fallback = (user?.modules || [])[0];
      return <Navigate to={fallback ? `/dashboard/${fallback}` : '/team'} replace />;
    }
  }

  const accent = module.accent;
  const ModuleIcon = module.icon;
  const allTools = MODULE_TOOLS[slug] || [];
  // Filter tools based on user's allowed_tools (per module). Super_admin sees all.
  // If allowed_tools[slug] is undefined → user inherits ALL org tools. If [] → no tools.
  const userAllowed = user?.allowed_tools?.[slug];
  const isAllowed = (toolKey) => {
    if (user?.role === 'super_admin') return true;
    if (userAllowed === undefined) return true;  // inherit all
    return userAllowed.includes(toolKey);
  };
  const tools = allTools.map((t) => ({ ...t, _allowed: isAllowed(t.key) }));

  // helper to render a single tool tile
  function renderTool(t, slug, accent) {
    const allowed = t._allowed;
    const live = t.live && allowed;
    const target = live && t.route ? `/dashboard/${slug}${t.route}` : null;
    const Wrap = target ? Link : 'div';
    const wrapProps = target ? { to: target } : {};
    const stateLabel = !allowed ? 'LOCKED' : (t.live ? 'LIVE' : 'COMING SOON');
    return (
      <Wrap key={t.key} {...wrapProps} className={`group relative bg-white p-4 rounded border transition-all overflow-hidden ${live ? 'border-neutral-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer hover:border-current' : 'border-dashed border-neutral-200 opacity-70 cursor-not-allowed'}`} style={live ? { '--tw-ring-color': accent } : {}} data-testid={`tool-${slug}-${t.key}`} title={!allowed ? 'You do not have access to this tool. Ask your admin.' : ''}>
        {live && <span className="absolute top-0 left-0 right-0 h-0.5" style={{ background: accent }} />}
        <div className="flex items-start justify-between mb-3">
          <div className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded" style={live ? { background: `${accent}15`, color: accent } : { background: !allowed ? '#FEE2E2' : '#F3F4F6', color: !allowed ? '#B91C1C' : '#9CA3AF' }}>
            {stateLabel}
          </div>
          {live && <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-current transition-colors" style={{ color: accent }} />}
        </div>
        <div className={`font-display font-semibold text-base leading-tight ${live ? 'text-navy' : 'text-neutral-400'}`}>{t.label}</div>
        <div className={`text-xs leading-relaxed mt-1 ${live ? 'text-neutral-600' : 'text-neutral-400'}`}>{t.desc}</div>
      </Wrap>
    );
  }


  return (
    <DashboardShell module={module} sectionTitle="Workspace">
      {/* Module hero — feels like a workspace, not a dashboard */}
      <div className="relative overflow-hidden rounded-md mb-6 border" style={{ background: `linear-gradient(135deg, ${accent}15, ${accent}03)`, borderColor: `${accent}30` }}>
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: accent }} />
        <div className="relative p-6 lg:p-7 flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            {module.logo ? (
              <div className="h-16 w-16 rounded-md bg-white border border-neutral-200 flex items-center justify-center overflow-hidden p-2">
                <img src={module.logo} alt="" className="max-h-12 object-contain" />
              </div>
            ) : (
              <div className="h-16 w-16 rounded-md flex items-center justify-center" style={{ background: `${accent}1A`, border: `1px solid ${accent}40`, color: accent }}>
                <ModuleIcon className="w-8 h-8" strokeWidth={1.5} />
              </div>
            )}
            <div>
              <div className="label-tag mb-1" style={{ color: accent }}>{user?.org_name || 'Workspace'} · Module</div>
              <h1 className="font-display font-bold text-2xl lg:text-3xl tracking-tight text-navy">{module.name}</h1>
              <p className="text-sm text-neutral-600 mt-0.5">{module.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tools[0]?.live && tools[0].route && (
              <Link to={`/dashboard/${slug}${tools[0].route}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-medium text-white text-sm transition-all hover:-translate-y-px" style={{ background: accent }} data-testid="primary-action">
                <Plus className="w-4 h-4" /> Open {tools[0].label}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* TOOLS GRID — primary view, grouped if module declares groups */}
      <div className="mb-6">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-display font-semibold text-xl text-navy">Module Tools</h2>
            <p className="text-sm text-neutral-500">All capabilities in this module · click any tool to open it.</p>
          </div>
          <span className="text-xs text-neutral-500 font-mono">{tools.filter(t => t.live).length}/{tools.length} live</span>
        </div>

        {/* If any tool has a group, render grouped */}
        {tools.some((t) => t.group) ? (
          (() => {
            const groups = {};
            tools.forEach((t) => {
              const g = t.group || 'Tools';
              groups[g] = groups[g] || [];
              groups[g].push(t);
            });
            return Object.entries(groups).map(([groupName, items]) => (
              <div key={groupName} className="mb-7">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-display font-semibold text-sm uppercase tracking-wider" style={{ color: accent }}>{groupName}</h3>
                  <span className="h-px flex-1" style={{ background: `${accent}30` }} />
                  <span className="text-[11px] font-mono text-neutral-500">{items.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {items.map((t) => renderTool(t, slug, accent))}
                </div>
              </div>
            ));
          })()
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {tools.map((t) => renderTool(t, slug, accent))}
          </div>
        )}
      </div>

      {/* Compact KPIs + recent records */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
        <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(summary?.kpis || []).map((k, i) => (
            <div key={i} className="kpi-tile" style={{ '--module-accent': accent }}>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">{k.label}</div>
              <div className="font-display text-2xl font-bold text-navy tabular-nums">{k.value}</div>
              <div className="text-[11px] text-neutral-500 mt-0.5">{k.sub}</div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-5 bg-white border border-neutral-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-base text-navy">Recent activity</h3>
            <Link to={`/dashboard/${slug}/records`} className="text-xs font-medium" style={{ color: accent }}>See all <ChevronRight className="w-3 h-3 inline" /></Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-sm text-neutral-500 py-4 text-center">No records yet.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {recent.slice(0, 4).map((r) => (
                <li key={r.id} className="py-2.5 first:pt-0 last:pb-0 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-navy text-sm truncate">{r.name || r.title || r.number || r.case_no || r.patient_no || r.admission_no || r.id}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5 font-mono">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  {r.status && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-neutral-100 text-neutral-700 flex-shrink-0">{r.status}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
